import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { DateInput } from '@/components/ui/DateInput'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { formatCurrency, formatDate, parseDateOnly } from '@/lib/formatters'
import type { QuoteInput } from '@/models/internalEndorsement'
import type { PolicyChangePolicy, PolicyChangeQuote } from '@/models/policyChange'
import { toIsoDate } from '../../PolicyChanges/policyChangeCopy'

const amount = (value: string | null) => (value === null ? null : Number(value))
const asAmount = (value: number | null) => (value === null ? null : String(value))
const money = (value: string | null) => (value === null ? '-' : formatCurrency(Number(value)))

interface EndorsementQuoteRowProps {
  quote: PolicyChangeQuote
  policy: PolicyChangePolicy | undefined
  /** Unsaved edits to this quote; absent when it shows read-only. */
  draft?: QuoteInput
  onChange?: (fields: QuoteInput) => void
}

/** One policy's quote: what its carrier said, entered while the request is out with the carriers. */
export function EndorsementQuoteRow({ quote, policy, draft, onChange }: EndorsementQuoteRowProps) {
  const title = policy
    ? [policy.coverages.join(', '), policy.carrierName].filter(Boolean).join(' · ')
    : 'Policy'

  if (!draft || !onChange) {
    return (
      <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 py-2 text-sm">
        <span>
          {title}
          {[
            quote.carrierReference,
            quote.effectiveDate && `From ${formatDate(quote.effectiveDate)}`,
            quote.expiresOn && `Good until ${formatDate(quote.expiresOn)}`,
          ]
            .filter(Boolean)
            .map((detail) => (
              <span key={detail} className="block text-xs text-neutral-500">
                {detail}
              </span>
            ))}
        </span>
        <span>{money(quote.proratedChange)}</span>
        <span>{money(quote.annualChange)}</span>
      </div>
    )
  }

  const value = <K extends keyof QuoteInput>(key: K) => (key in draft ? draft[key] : quote[key]) ?? null
  const id = (field: string) => `quote-${quote.id}-${field}`
  return (
    <div className="flex flex-col gap-3 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium">
          {title}
          {policy?.policyNumber && <span className="text-neutral-500"> · {policy.policyNumber}</span>}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field>
          <FieldLabel htmlFor={id('prorated')}>Due now</FieldLabel>
          <CurrencyInput
            id={id('prorated')}
            allowNegative
            value={amount(value('proratedChange'))}
            onChange={(next) => onChange({ proratedChange: asAmount(next) })}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={id('annual')}>Yearly change</FieldLabel>
          <CurrencyInput
            id={id('annual')}
            allowNegative
            value={amount(value('annualChange'))}
            onChange={(next) => onChange({ annualChange: asAmount(next) })}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={id('total')} optional>
            New total premium
          </FieldLabel>
          <CurrencyInput
            id={id('total')}
            value={amount(value('newTotalPremium'))}
            onChange={(next) => onChange({ newTotalPremium: asAmount(next) })}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={id('reference')} optional>
            Carrier reference
          </FieldLabel>
          <Input
            id={id('reference')}
            value={value('carrierReference') ?? ''}
            onChange={(event) => onChange({ carrierReference: event.target.value })}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={id('effective')} optional>
            Effective
          </FieldLabel>
          <DateInput
            value={parseDateOnly(value('effectiveDate'))}
            onChange={(date) => onChange({ effectiveDate: toIsoDate(date) })}
            inputProps={{ id: id('effective') }}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={id('expires')} optional>
            Good until
          </FieldLabel>
          <DateInput
            value={parseDateOnly(value('expiresOn'))}
            onChange={(date) => onChange({ expiresOn: toIsoDate(date) })}
            inputProps={{ id: id('expires') }}
          />
        </Field>
      </div>
    </div>
  )
}
